import { Query } from '@apollo/client/react/components'
import MonetaryValue from '@eversports/react-components/Legacy/MonetaryValue'
import CircularProgress from '@eversports/react-components/primitives/CircularProgress'
import Flex from '@eversports/react-components/primitives/Flex'
import FormatDate from '@eversports/react-components/primitives/FormatDate'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Placeholder from '@eversports/react-components/primitives/Placeholder'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import { parse } from 'query-string'
import React from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import {
  RegistrationTermsAndConditionsDataContractInfoDocument,
  RegistrationTermsAndConditionsDataContractInfoQuery,
} from '../../graphql'

import { Localized } from '../../localization/react'

import RegistrationTermsAndConditionsDataSubject from './RegistrationTermsAndConditionsDataSubject'

const useStyles = makeStyles({
  infoBanner: {
    borderRadius: '4px',
    backgroundColor: '#eeeaf8',
    padding: '12px 16px',
    marginBottom: '16px',
  },
})

const RegistrationTermsAndConditionsData: React.FC<React.PropsWithChildren<RouteComponentProps>> = ({ location }) => {
  const { token } = parse(location.search)
  const classes = useStyles()

  if (!token) {
    throw new Error('The required token was not defined')
  }

  return (
    <Query<RegistrationTermsAndConditionsDataContractInfoQuery>
      query={RegistrationTermsAndConditionsDataContractInfoDocument}
      variables={{ token }}
    >
      {({ error, data }) => {
        if (error) {
          return <>{error.message}</>
        }

        if (data) {
          const {
            start,
            end,
            subscriptionPackage,
            subscriptionPeriod,
            contractDurationYears,
            subscriptionFee: { value, currency },
            subscriptionDiscountStart,
            subscriptionDiscountEnd,
            subscriptionDiscountAmount,
            subscriptionDiscountType,
            originalContractStartDate,
          } = data.contractInfo

          const moneyValue = subscriptionDiscountAmount
            ? subscriptionDiscountType === 'fixed_amount'
              ? value - subscriptionDiscountAmount
              : value * (1 - subscriptionDiscountAmount / 100)
            : 0

          const hasSubscriptionDiscount = Boolean(
            subscriptionDiscountAmount && subscriptionDiscountAmount > 0 && moneyValue > 0,
          )

          const sharedDiscountParams = {
            money: <MonetaryValue value={moneyValue} currencyCode={currency} />,
            type: subscriptionDiscountType === 'fixed_amount' ? currency : '%',
            discount: subscriptionDiscountAmount,
            start: subscriptionDiscountStart && <FormatDate variant="medium" value={subscriptionDiscountStart} />,
          }

          return (
            <Flex flexDirection="column" verticalSpacing="dense" fullWidth>
              {originalContractStartDate && (
                <div className={classes.infoBanner}>
                  <Flex horizontalSpacing="dense" alignItems="flex-start">
                    <Icon icon={Icons.INFO} color="info.main" size="default" />
                    <Flex flexDirection="column" verticalSpacing="dense">
                      <Text fontWeight="bold">
                        <Localized id="registration-terms-and-conditions-date-adjusted-title" />
                      </Text>
                      <Text size="small">
                        <Localized
                          id="registration-terms-and-conditions-date-adjusted-message"
                          params={{
                            newDate: (
                              <strong>
                                <FormatDate variant="medium" value={new Date(start)} />
                              </strong>
                            ),
                            originalDate: <FormatDate variant="medium" value={new Date(originalContractStartDate)} />,
                          }}
                        />
                      </Text>
                    </Flex>
                  </Flex>
                </div>
              )}
              <Heading is="h2">
                <Localized id="registration-terms-and-conditions-heading" />
              </Heading>
              <Flex flexDirection="column" verticalSpacing="dense" maxWidth="368px" fullWidth>
                <RegistrationTermsAndConditionsDataSubject
                  subject={<Localized id="registration-terms-and-conditions-package" />}
                  value={<Localized id={`billing-pricing-option-${subscriptionPackage.toLowerCase()}` as any} />}
                />
                <RegistrationTermsAndConditionsDataSubject
                  subject={<Localized id="registration-terms-and-conditions-contract-start" />}
                  value={<FormatDate variant="medium" value={start} />}
                />
                <RegistrationTermsAndConditionsDataSubject
                  subject={<Localized id="registration-terms-and-conditions-contract-end" />}
                  value={<FormatDate variant="medium" value={end} />}
                />
                <RegistrationTermsAndConditionsDataSubject
                  subject={<Localized id="registration-terms-and-conditions-contract-duration" />}
                  value={
                    <Localized
                      id={'registration-terms-and-conditions-duration-months'}
                      params={{ contractDurationMonths: contractDurationYears * 12 }}
                    />
                  }
                />
                {value > 0 && (
                  <RegistrationTermsAndConditionsDataSubject
                    subject={<Localized id="registration-terms-and-conditions-rate" />}
                    value={
                      <Localized
                        id="registration-terms-and-conditions-per"
                        params={{
                          charge: <MonetaryValue value={value} currencyCode={currency} />,
                          period: subscriptionPeriod,
                        }}
                      />
                    }
                  />
                )}
                {hasSubscriptionDiscount && (
                  <RegistrationTermsAndConditionsDataSubject
                    subject={<Localized id="registration-terms-and-conditions-discount" />}
                    value={
                      subscriptionDiscountEnd ? (
                        <Localized
                          id="registration-terms-and-conditions-discount-value-type"
                          params={{
                            ...sharedDiscountParams,
                            end: <FormatDate variant="medium" value={subscriptionDiscountEnd} />,
                          }}
                        />
                      ) : (
                        <Localized
                          id="registration-terms-and-conditions-discount-value-type-unlimited"
                          params={sharedDiscountParams}
                        />
                      )
                    }
                  />
                )}
              </Flex>
            </Flex>
          )
        }

        return (
          <Placeholder height={250} width="100%" center>
            <CircularProgress />
          </Placeholder>
        )
      }}
    </Query>
  )
}
export default withRouter(RegistrationTermsAndConditionsData)
